// Pure helpers for locating, reading, and replacing the vitals block inside a generated clinical note; kept free of React/DOM dependencies so they can be unit tested in isolation (see noteVitals.test.ts)

export interface VitalsBlockRange {
  startIndex: number;
  endIndex: number;
}

// Matches the start of a vitals block such as "Vitals:", "Vital Signs:", "VITALS:", or "Vital Signs (Mock CHAPI):"; anchored to the start of a line
const VITALS_HEADER_REGEX = /^\s*(?:Vitals|Vital Signs)(?:\s*\(Mock CHAPI\))?:/i;

// Known section headers that terminate the vitals block even when they are not written in ALL-CAPS; compared case-insensitively against the text before the first colon on a line
const KNOWN_SECTION_HEADERS = new Set([
  "physical examination",
  "physical exam",
  "objective",
  "subjective",
  "assessment/plan",
  "assessment",
  "plan",
  "hpi",
  "ros",
  "labs",
  "active problems",
  "medications",
  "allergies",
]);

const PHYSICAL_EXAM_HEADER_REGEX = /^\s*physical exam(?:ination)?\s*:/i;
const OBJECTIVE_HEADER_REGEX = /^\s*objective\s*:/i;
const MOCK_CHAPI_MARKER_REGEX = /\(Mock CHAPI\)/i;

// A line is treated as a section header (and therefore a vitals-block boundary) when it is either an ALL-CAPS label ending in a colon such as "LABS:", "PHYSICAL EXAM:", "ACTIVE PROBLEMS:" or one of the known Title/Case headers
export const isSectionHeaderLine = (line: string | null | undefined): boolean => {
  const trimmed = (line || "").trim();
  if (!trimmed) return false;

  const colonIndex = trimmed.indexOf(":");
  if (colonIndex <= 0) return false;

  const label = trimmed.slice(0, colonIndex).trim();
  if (!label) return false;

  if (KNOWN_SECTION_HEADERS.has(label.toLowerCase())) {
    return true;
  }

  const afterColon = trimmed.slice(colonIndex + 1).trim();
  const isAllCaps = /[A-Z]/.test(label) && !/[a-z]/.test(label);
  // Require nothing meaningful after the colon so inline "Key: value" lines are not mistaken for section headers
  return isAllCaps && afterColon === "";
};

const isVitalsHeaderLine = (line: string | null | undefined): boolean =>
  VITALS_HEADER_REGEX.test(line || "");

// Locates the [start, end) line index range covering the vitals block; the block starts at the first vitals header line and extends through any immediately following related lines (wrapped values, the "(Vital signs from ...)" note), stopping at the first blank line or the next section header
export const findVitalsBlockRange = (lines: string[]): VitalsBlockRange | null => {
  const startIndex = lines.findIndex((line) => isVitalsHeaderLine(line));
  if (startIndex === -1) return null;

  let endIndex = startIndex + 1;
  while (endIndex < lines.length) {
    const line = lines[endIndex];
    if (!line.trim()) break; // Blank line terminates the block
    if (isSectionHeaderLine(line)) break; // Next section header terminates the block
    endIndex += 1;
  }

  return { startIndex, endIndex };
};

// Returns the vitals block found in a note (header plus values), trimmed, or an empty string when no vitals block is present; shares boundary logic with replaceVitalsSection so vitals-outdated detection and replacement agree on what the block is
export const getVitalsSectionFromNote = (noteText: unknown): string => {
  const normalizedNote = typeof noteText === "string" ? noteText : String(noteText || "");
  if (!normalizedNote.trim()) return "";

  const lines = normalizedNote.split("\n");
  const range = findVitalsBlockRange(lines);
  if (!range) return "";

  return lines.slice(range.startIndex, range.endIndex).join("\n").trim();
};

// Normalizes the incoming vitals value so we never emit a doubled header; values coming from chart_history already begin with "VITALS:", anything else gets a single "Vitals:" header prefixed
const normalizeVitalsBlock = (nextVitalsValue: string): string => {
  const trimmed = String(nextVitalsValue).replace(/\s+$/, "").replace(/^\n+/, "");
  if (isVitalsHeaderLine(trimmed)) {
    return trimmed;
  }
  return `Vitals:\n${trimmed}`;
};

// When the note we are replacing carried a "(Mock CHAPI)" marker on its vitals header, keep that marker on the replacement header so the data-source indicator is not silently dropped
const preserveMockChapiMarker = (
  blockLines: string[],
  originalHeaderLine: string | undefined
): string[] => {
  if (!MOCK_CHAPI_MARKER_REGEX.test(originalHeaderLine || "")) return blockLines;
  if (!blockLines.length || MOCK_CHAPI_MARKER_REGEX.test(blockLines[0])) return blockLines;

  const nextLines = [...blockLines];
  // Insert the marker just before the header's first colon
  nextLines[0] = nextLines[0].replace(":", " (Mock CHAPI):");
  return nextLines;
};

const insertAfterHeader = (
  lines: string[],
  headerRegex: RegExp,
  blockLines: string[]
): string | null => {
  const headerIndex = lines.findIndex((line) => headerRegex.test(line));
  if (headerIndex === -1) return null;

  const next = lines.slice(0, headerIndex + 1);
  next.push(...blockLines);
  next.push(...lines.slice(headerIndex + 1));
  return next.join("\n");
};

// Reliably locates and REPLACES the existing vitals block regardless of the surrounding headers; when no vitals block exists, inserts the block after the PHYSICAL EXAM header (or OBJECTIVE), falling back to appending at the end
export const replaceVitalsSection = (
  noteText: string | null | undefined,
  nextVitalsValue: string | null | undefined
): string => {
  if (!noteText || !nextVitalsValue) return noteText || "";

  const normalizedVitalsBlock = normalizeVitalsBlock(nextVitalsValue);
  const blockLines = normalizedVitalsBlock.split("\n");

  const lines = noteText.split("\n");
  const range = findVitalsBlockRange(lines);

  if (range) {
    const replacementLines = preserveMockChapiMarker(blockLines, lines[range.startIndex]);
    const next = [
      ...lines.slice(0, range.startIndex),
      ...replacementLines,
      ...lines.slice(range.endIndex),
    ];
    return next.join("\n");
  }

  const insertedAfterExam = insertAfterHeader(lines, PHYSICAL_EXAM_HEADER_REGEX, blockLines);
  if (insertedAfterExam !== null) return insertedAfterExam;

  const insertedAfterObjective = insertAfterHeader(lines, OBJECTIVE_HEADER_REGEX, blockLines);
  if (insertedAfterObjective !== null) return insertedAfterObjective;

  return `${noteText.trimEnd()}\n\n${normalizedVitalsBlock}`;
};
